import { Base, FetchingServices } from '@/composables/base';
import { reactive } from 'vue';

//  |
//  |   Department Types
// @@@
//  @
export type TDepartmentMember = {
  created_at: string;
  department_id: number;
  id: number;
  is_admin: boolean;
  phone: string;
};

type TDepartment = {
  created_at: string;
  description: string;
  dtkn: string;
  id: number;
  name: string;
  str_safe_id: string;
  updated_at: string;
};

export type TDepartmentModel = {
  department: TDepartment;
  members: TDepartmentMember[];
};

export type TDepartmentForm = {
  department: Pick<TDepartment, 'description' | 'name' | 'str_safe_id'>;
  members: Array<Pick<TDepartmentMember, 'is_admin' | 'phone'>>;
};

export type TDepartmentUpdateModel = {
  add_members: TDepartmentMember[];
  department_id: number;
  description: string;
  name: string;
  remove_members: string[];
  str_safe_id: string;
};

//  |
//  |   Filter Criterias type
// @@@
//  @
type TFilterCriterias = {
  name_or_description?: string;
  page_number: number;
  page_size: number;
};

//  |
//  |   Interface and Class
// @@@
//  @
interface IDepartment {
  departmentList: TDepartmentModel[];
  departmentById: TDepartmentModel | null;
  filterCriterias: TFilterCriterias;
  isFilterOn: boolean;

  managerList: TDepartmentMember[];

  // API
  getDepartmentListApi(): Promise<TDepartmentModel[] | []>;
  postNewDepartmentApi(formData: Partial<TDepartmentModel>): Promise<boolean>;
  deleteDepartmentApi(id: number): void;

  extractDepartmentByIdFromDepartmentList(id: number): void;
  addMembers(department_id: number, newMembers: TDepartmentMember[]): void;
  deleteMemeberBulk(tment_id: number, phones: string[]): void;

  updateDepartmentMemberTableAfterAddMember(): void;
  updateManagerList(): void;

  // Setters
  setDepartmentList(list: TDepartmentModel[]): void;

  // Getters
  getDepartmentList(): TDepartmentModel[];
  getManagerList(): TDepartmentMember[];
}
export class Department extends Base implements IDepartment {
  departmentList: TDepartmentModel[] = [];
  departmentById: TDepartmentModel | null = null;

  filterCriterias: TFilterCriterias = {
    name_or_description: undefined,
    page_number: 1,
    page_size: 300,
  };
  isFilterOn: boolean = false;

  managerList: TDepartmentMember[] = [];

  // API
  async getDepartmentListApi() {
    try {
      // NOTE: 500 series errors continue the try block and are handled on global error service
      let res = await this.post<TDepartmentModel[]>(
        FetchingServices.ipbx,
        'department/list',
        {}
      );
      if (res.status === 200 && res.data) {
        this.setDepartmentList(res.data);
        return res.data;
      } else return [];
    } catch (err) {
      // handel error
      console.log('get dep err', err);
      return [];
    }
  }
  async postNewDepartmentApi(
    formData: DeepPartial<TDepartmentModel>
  ): Promise<boolean> {
    try {
      const res = await this.post(FetchingServices.ipbx, '/department/add', {
        ...this.generatePayload(formData),
      });

      if (res.status === 200) {
        // toast success
        return true;
      }
      return false;
    } catch (err) {
      // handel error
      console.log('post dep err', err);
      return false;
    }
  }
  async deleteDepartmentApi(id: number): Promise<boolean> {
    try {
      const res = await this.delete(FetchingServices.ipbx, `/department/${id}`);
      if (res.status === 200) {
        return true;
      } else return false;
    } catch (err) {
      // handel error
      return false;
    }
  }
  async patchDepartmentByIdApi(
    updateValue: DeepPartial<TDepartmentUpdateModel>
  ): Promise<boolean> {
    try {
      const res = await this.patch(FetchingServices.ipbx, `/department`, {
        ...updateValue,
      });
      if (res.status === 200) {
        // toast success
        return true;
      } else return false;
    } catch (err) {
      // handel error
      console.log('patch dep err', err);
      return false;
    }
  }

  // Private function
  private generatePayload(formData: DeepPartial<TDepartmentModel>) {
    const fd = formData;
    return {
      description: fd.department?.description,
      members: fd.members,
      name: fd.department?.name,
      str_safe_id: fd.department?.str_safe_id,
    };
  }

  // Public function
  public extractDepartmentByIdFromDepartmentList(id: number) {
    this.departmentById =
      this.departmentList.find((dep) => dep.department.id === id) || null;
  }
  public async addMembers(
    department_id: number,
    newMembers: TDepartmentMember[]
  ): Promise<boolean> {
    return await this.patchDepartmentByIdApi({
      department_id,
      add_members: newMembers,
    });
  }
  async deleteMemeberBulk(
    department_id: number,
    phones: string[]
  ): Promise<boolean> {
    return await this.patchDepartmentByIdApi({
      department_id,
      remove_members: phones,
    });
  }

  async updateDepartmentMemberTableAfterAddMember() {
    // TODO: unhandled error
    if (!this.departmentById) return;

    await this.getDepartmentListApi();
    this.extractDepartmentByIdFromDepartmentList(
      this.departmentById?.department.id
    );
  }
  updateManagerList() {
    this.managerList = this.departmentList.flatMap(
      (dep) => dep.members.filter((m) => m.is_admin) || []
    );
  }

  // Setters
  public setDepartmentList(list: TDepartmentModel[]) {
    this.departmentList = [...list];
    this.updateManagerList();
  }

  // getters
  public getDepartmentList(): TDepartmentModel[] {
    return [...this.departmentList];
  }
  public getManagerList(): TDepartmentMember[] {
    return [...this.managerList];
  }
}
export const useSettingsDepartment = reactive(new Department());
